import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import type {
  AthenaEvent,
  AthenaEventCategory,
  AthenaEventType,
} from "@evidenceb/athena-event-storage-schemas";
import {
  AthenaEventStream,
  type LastSyncMarkerWriteError,
  type Milliseconds,
  type ReplicationPullError,
  type ReplicationPushNonRetryableError,
  type ReplicationPushRetryableError,
  type ReplicationPushWrongBatchSizeError,
  type SubscriberProcessAthenaEventError,
} from "@evidenceb/athena-events-tools";
import { createDebugLogger, startLoadPhase } from "@evidenceb/debug-utils";
import * as Sentry from "@sentry/react";
import safeStringify from "fast-safe-stringify";
import React, {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import {
  PULL_INTERVAL,
  RETRY_FAILED_PUSH_OR_PULL_AFTER,
} from "../const/AthenaEventStream";
import useEventStorageClient from "../hooks/useEventStorageClient";
import { convertibleAthenaEventTypes } from "../utils/statements/from-athena-event";
import { sessionStore } from "./SessionContext";

// Diagnostic logs, disabled by default; enable from the browser console with
// localStorage.debug = "athena-event-stream"
const debug = createDebugLogger("athena-event-stream");

export const EventStreamContext = React.createContext<AthenaEventStream | null>(
  null,
);

// Used for e2e tests
export let _currentAthenaEventStream: AthenaEventStream | null = null;

export function useInitializingEventStream() {
  return useContext(EventStreamContext);
}

export function useAthenaEventStream(): AthenaEventStream {
  const athenaEventStream = useInitializingEventStream();
  if (!athenaEventStream) throw new Error("AthenaEventStream not initialized");
  return athenaEventStream;
}

export function useAthenaEventsRecentlySynced(delta: Milliseconds): boolean {
  const athenaEventStream = useInitializingEventStream();
  const [recentlySynced, setRecentlySynced] = useState(false);
  useEffect(() => {
    athenaEventStream
      ?.waitForRecentSync(delta)
      .then(() => setRecentlySynced(true));
  }, [athenaEventStream, delta]);
  return recentlySynced;
}

/**
 * Function publishing an AthenaEvent to the AthenaEventStream that
 * auto-populates some AthenaEvent properties so that the caller doesn't need to
 * supply them.
 */
export type AthenaEventStreamPublisher = <
  C extends AthenaEventCategory,
  T extends AthenaEventType,
>(
  athenaEvent: Omit<
    AthenaEvent<C, T>,
    "id" | "createdAt" | "userId" | "sessionId" | "app"
  >,
) => void;

export function useAthenaEventStreamPublisher(): AthenaEventStreamPublisher {
  const athenaEventStream = useInitializingEventStream();
  const { session } = useContext(sessionStore);
  return useMemo(
    () => (athenaEvent) =>
      athenaEventStream?.publish({
        id: uuid(),
        createdAt: new Date().toISOString(),
        sessionId: session.sessionId,
        userId: session.userId,
        app: session.version,
        ...athenaEvent,
      }),
    [athenaEventStream, session],
  );
}

function useInitAthenaEventStream() {
  const [aes, setAes] = useState<AthenaEventStream | null>(null);
  const { session } = useContext(sessionStore);
  const eventStorageClient = useEventStorageClient();
  const pullAllAthenaEvents = useFeatureFlag("pullAllAthenaEvents");
  // Diagnostic-only: lets us log exactly which dependency changed each time
  // this effect re-fires, since a re-fire tears down and recreates the
  // AthenaEventStream (and its underlying RxDB database) for this user.
  const prevDepsRef = useRef<{
    eventStorageClient: typeof eventStorageClient;
    userId: string;
    pullAllAthenaEvents: boolean;
  } | null>(null);
  useEffect(() => {
    const prevDeps = prevDepsRef.current;
    const changedDeps = prevDeps
      ? {
          eventStorageClient:
            prevDeps.eventStorageClient !== eventStorageClient,
          userId: prevDeps.userId !== session.userId,
          pullAllAthenaEvents:
            prevDeps.pullAllAthenaEvents !== pullAllAthenaEvents,
        }
      : null;
    prevDepsRef.current = {
      eventStorageClient,
      userId: session.userId,
      pullAllAthenaEvents,
    };
    debug.info("effect fired", {
      isFirstRun: prevDeps === null,
      changedDeps,
    });
    if (!eventStorageClient || !session.userId) {
      debug.info("init skipped", {
        hasEventStorageClient: Boolean(eventStorageClient),
        hasUserId: Boolean(session.userId),
      });
      return;
    }
    let disposed = false;
    debug.info("init start", {
      pullAllAthenaEvents,
      pullInterval: PULL_INTERVAL,
      retryFailedPushOrPullAfter: RETRY_FAILED_PUSH_OR_PULL_AFTER,
    });
    const athenaEventStream = new AthenaEventStream(
      eventStorageClient,
      session.userId,
      {
        onReplicationPullError(error: ReplicationPullError) {
          debug.error("replication pull error", {
            cause: safeStringify(error.cause),
          });
          Sentry.captureException(error, {
            extra: { cause: safeStringify(error.cause) },
          });
        },
        onLastSyncMarkerWriteError(error: LastSyncMarkerWriteError) {
          debug.error("last sync marker write error", {
            cause: safeStringify(error.cause),
            ...error.context,
          });
          Sentry.captureException(error, {
            extra: { cause: safeStringify(error.cause), ...error.context },
          });
        },
        onSubscriberProcessAthenaEventError(
          error: SubscriberProcessAthenaEventError,
        ) {
          Sentry.captureException(error, {
            extra: {
              athenaEvent: safeStringify(error.athenaEvent),
              subscriberId: error.subscriberId,
              error: safeStringify(error.error),
            },
          });
        },
        onReplicationPushWrongBatchSizeError(
          error: ReplicationPushWrongBatchSizeError,
        ) {
          Sentry.captureException(error, {
            extra: { batchSize: error.batchSize },
          });
        },
        onReplicationPushRetryableError(error: ReplicationPushRetryableError) {
          debug.info("replication push retryable error", {
            reason: error.athenaEventSaveResult.error.reason,
            details: safeStringify(error.athenaEventSaveResult.error.details),
          });
          Sentry.captureException(error, {
            extra: {
              athenaEventSaveResult: safeStringify(error.athenaEventSaveResult),
            },
          });
        },
        onReplicationPushNonRetryableError(
          error: ReplicationPushNonRetryableError,
        ) {
          debug.error("replication push non-retryable error", {
            reason: error.athenaEventSaveResult.error.reason,
            details: safeStringify(error.athenaEventSaveResult.error.details),
          });
          Sentry.captureException(error, {
            extra: {
              athenaEventSaveResult: safeStringify(error.athenaEventSaveResult),
            },
          });
        },
        onReplicationPushUniqueViolation(info) {
          Sentry.captureMessage("replication push unique violation", {
            level: "info",
            extra: {
              id: info.athenaEvent.id,
              type: info.athenaEvent.type,
              instanceId: info.instanceId,
              isLeader: info.isLeader,
              documentHidden: info.documentHidden,
            },
          });
        },
      },
      !pullAllAthenaEvents
        ? [...convertibleAthenaEventTypes, "AIShowMessageEvent"]
        : undefined,
      PULL_INTERVAL,
      RETRY_FAILED_PUSH_OR_PULL_AFTER,
    );
    const start = async () => {
      try {
        debug.info("start() begin", {
          instanceId: athenaEventStream.__instanceId,
        });
        const endPhase = startLoadPhase("event-stream.start");
        await athenaEventStream.start();
        endPhase(athenaEventStream.__startTimings ?? undefined);
        debug.info("start() resolved", {
          instanceId: athenaEventStream.__instanceId,
          hasRxdb: Boolean(athenaEventStream.__rxdb),
          hasReplicationState: Boolean(athenaEventStream.__replicationState),
        });
        if (!disposed) {
          _currentAthenaEventStream = athenaEventStream;
          setAes(athenaEventStream);
        }
      } catch (error) {
        console.error("[athena-event-stream] start() failed", {
          instanceId: athenaEventStream.__instanceId,
          error,
        });
        Sentry.captureException(error, {
          extra: { phase: "athena-event-stream-start" },
        });
      }
    };
    start();
    return () => {
      disposed = true;
      _currentAthenaEventStream = null;
      debug.info("stop() begin", {
        instanceId: athenaEventStream.__instanceId,
      });
      athenaEventStream.stop().catch((error) => {
        console.error("[athena-event-stream] stop() failed", {
          instanceId: athenaEventStream.__instanceId,
          error,
        });
        Sentry.captureException(error, {
          extra: { phase: "athena-event-stream-stop" },
        });
      });
    };
  }, [eventStorageClient, session.userId, pullAllAthenaEvents]);
  return aes;
}

export function EventStreamProvider({ children }: { children: ReactNode }) {
  const athenaEventStream = useInitAthenaEventStream();
  return (
    <EventStreamContext.Provider value={athenaEventStream}>
      {children}
    </EventStreamContext.Provider>
  );
}
